import { userAuthContextType } from '../../auth/AuthProvider';
import { axios, getResponse, settings } from '../../helpers/main';

const { backend } = settings;
const authServices = { user: userAuthContextType }; //eslint-disable-line
const objectServices = getResponse({ type: 0 }); //eslint-disable-line

/**
 * Service to manage communication with the Requirements API.
 * Follows the Single Responsibility Principle (SRP) by separating network logic from the UI.
 */
export const RequirementService = {
    /**
     * Retrieves all Requirements.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectServices>} Promise with the API response.
     */
    getAll: async ({
        auth,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.requirements}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                method: backend?.methods?.requirements.getAll,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Retrieves data for adding a Requirement (catalogues, etc).
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectServices>} Promise with the API response.
     */
    getAdd: async ({
        auth,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.requirements}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                method: backend?.methods?.requirements.getAdd,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Deletes a Requirement by ID.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number|string} params.id - Requirement ID to delete.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectServices>} Promise with the API response.
     */
    deleteAsync: async ({
        auth,
        id,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.requirements}/${id}`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.delete(requestObj);
    },

    /**
     * Creates a new Requirement.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {Object} params.data - Requirement data.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectServices>} Promise with the API response.
     */
    create: async ({
        auth,
        data,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.requirements}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                data: data,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Retrieves a Requirement by ID.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number|string} params.id - Requirement ID.
     * @param {number} params.languageId - Current language ID.
     * @param {boolean} [params.withDetails=true] - Whether to include details.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectServices>} Promise with the API response.
     */
    getById: async ({
        auth,
        id,
        languageId,
        withDetails = true,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.requirements}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                id: id,
                languageId: languageId,
                method: backend?.methods?.requirements.getById,
                withDetails: withDetails,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Updates an existing Requirement.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {Object} params.data - Requirement data.
     * @param {number|string} params.id - Requirement ID.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectServices>} Promise with the API response.
     */
    update: async ({
        auth,
        data,
        id,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.requirements}/${id}`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                data: data,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Retrieves a single Requirement Detail by ID.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number|string} params.id - Requirement Detail ID.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectServices>} Promise with the API response.
     */
    getDetailById: async ({
        auth,
        id,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.requirementDetails}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                id: id,
                languageId: languageId,
                method: backend?.methods?.requirementDetails.getById,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Deletes a Requirement Detail by ID.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number|string} params.id - Requirement Detail ID.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectServices>} Promise with the API response.
     */
    deleteDetail: async ({
        auth,
        id,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.requirementDetails}/${id}`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.delete(requestObj);
    },

    /**
     * Creates a new Requirement Detail.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {Object} params.data - Requirement Detail data.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectServices>} Promise with the API response.
     */
    createDetail: async ({
        auth = authServices,
        data,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.requirementDetails}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                data: data,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Updates an existing Requirement Detail.
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {Object} params.data - Requirement Detail data.
     * @param {number|string} params.id - Requirement Detail ID.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectServices>} Promise with the API response.
     */
    updateDetail: async ({
        auth = authServices,
        data,
        id,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.requirementDetails}/${id}`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                data: data,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Retrieves a Requirement report (PDF).
     * @param {Object} params - Parameters.
     * @param {authServices} params.auth - User authentication object.
     * @param {number|string} params.id - Requirement ID.
     * @param {string} params.languageCode - Current language code.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectServices>} Promise with the API response.
     */
    getReport: async ({
        auth,
        id,
        languageCode,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.requirements}/${backend?.methods?.main.reports.pdf}`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                id: id,
                languageCode: languageCode,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },
};
